import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import knustlogo from "../assets/icons/knustlogo.png";
import gess from "../assets/images/gessLogo.png";

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showAdminLink, setShowAdminLink] = useState(false);
  const [tapCount, setTapCount] = useState(0);
  const [lastTapTime, setLastTapTime] = useState(0);

  // Secret key combination for admin access (Ctrl+A)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if Ctrl+A is pressed (key code 65 is 'a')
      if (e.ctrlKey && e.key === "a") {
        e.preventDefault(); // Prevent default Ctrl+A behavior (select all)
        setShowAdminLink(true);

        // Auto-hide admin link after 10 seconds
        setTimeout(() => {
          setShowAdminLink(false);
        }, 10000);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleLinkClick = () => {
    setMenuOpen(false); // Close the menu when a link is clicked
  };

  // Handle the logo tap for mobile admin access
  const handleLogoTap = () => {
    const currentTime = new Date().getTime();

    // If it's been more than 2 seconds since last tap, reset count
    if (currentTime - lastTapTime > 2000) {
      setTapCount(1);
    } else {
      setTapCount(tapCount + 1);
    }

    setLastTapTime(currentTime);

    // If tapped 3 times in quick succession, show admin link
    if (tapCount === 2) {
      setShowAdminLink(true);
      setTapCount(0);

      // Auto-hide admin link after 10 seconds
      setTimeout(() => {
        setShowAdminLink(false);
      }, 10000);
    }
  };

  // Links array - Admin link will be hidden unless activated
  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Events", path: "/events" },
    { name: "Articles", path: "/articles" },
    { name: "Contact Us", path: "/contact" },
  ];

  // Admin link (separate for conditional rendering)
  const adminLink = { name: "Admin Access", path: "/adminpage" };

  return (
    <header className="bg-white shadow-md">
      {/* Top Section */}
      <div className="flex items-center justify-between p-4 md:px-8">
        {/* Logos and Title with click handler for mobile admin access */}
        <div className="flex items-center space-x-4">
          <img
            className="w-16 h-16 md:w-28 md:h-24 cursor-pointer"
            src={knustlogo}
            alt="KNUST logo"
            onClick={handleLogoTap}
          />
          <img
            className="w-16 h-16 md:w-24 md:h-24"
            src={gess}
            alt="GESS logo"
          />
          <div className="hidden md:block text-black">
            <p className="text-lg md:text-2xl font-medium">
              Department of Geological Engineering
            </p>
            <p className="text-xs md:text-sm font-normal">
              Kwame Nkrumah University of Science and Technology
            </p>
          </div>
        </div>

        {/* Hamburger Menu for Mobile */}
        <div className="md:hidden">
          <button
            className="text-black focus:outline-none"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              {menuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Navigation Links for Desktop */}
        <nav className="hidden md:flex items-center space-x-6">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              className="text-black hover:text-blue-700 font-medium"
              to={link.path}
            >
              {link.name}
            </Link>
          ))}

          {/* Admin link that only appears when secret combo is pressed */}
          {showAdminLink && (
            <Link
              className="text-blue-700 font-bold animate-pulse"
              to={adminLink.path}
            >
              {adminLink.name}
            </Link>
          )}
        </nav>
      </div>

      {/* Enhanced Navigation Menu for Mobile */}
      <div
        className={`${
          menuOpen ? "max-h-96" : "max-h-0"
        } md:hidden overflow-hidden transition-all duration-300 ease-in-out bg-gradient-to-b from-blue-800 to-blue-900`}
      >
        <ul className="flex flex-col text-white p-4 divide-y divide-blue-700">
          {navLinks.map((link) => (
            <li key={link.name} className="py-2">
              <Link
                className="block hover:text-gray-300 transition-all duration-200 transform hover:translate-x-2"
                to={link.path}
                onClick={handleLinkClick}
              >
                {link.name}
              </Link>
            </li>
          ))}

          {/* Admin link in mobile that only appears when secret combo is pressed */}
          {showAdminLink && (
            <li className="py-2 border-t border-blue-600">
              <Link
                className="block text-yellow-300 font-bold animate-pulse"
                to={adminLink.path}
                onClick={handleLinkClick}
              >
                {adminLink.name}
              </Link>
            </li>
          )}

          {/* Hidden "More" option at the bottom of the mobile menu */}
        </ul>
      </div>
    </header>
  );
}

export default Header;
